import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'shared/contexts/AuthContext';
import { updateUserAccountFields } from 'shared/services/users';
import { Customer } from 'domains/customers/model/customer';
import { Manager } from 'domains/customers/model/manager';
import { getCustomer } from 'domains/customers/data/customers';
import { getManagerListByUserAccountId } from 'domains/customers/data/managers';

export interface CustomerAccountOption {
  customerId: string;
  customerName: string;
  manager: Manager;
}

export interface CustomerPickerViewModel {
  accounts: CustomerAccountOption[];
  currentCustomerId: string | null;
  currentCustomerName: string;
  loading: boolean;
  error: string | null;
  switchCustomer: (account: CustomerAccountOption) => Promise<void>;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to load customer accounts.';
}

function toAccountOption(manager: Manager, customer: Customer): CustomerAccountOption {
  return {
    customerId: customer.id,
    customerName: customer.name,
    manager,
  };
}

/**
 * Port of aligned's `MultiManagerButtonViewModel` and `MultiManagerViewModel`.
 * The manager list is loaded for the signed-in account, each customer is
 * resolved before it is shown, and switching persists only currentCustomerId.
 * ManagerContext/AuthContext subscriptions then re-resolve the active manager
 * and refresh the manager homepage for the selected customer.
 */
export function useCustomerPickerViewModel(): CustomerPickerViewModel {
  const { userAccount } = useAuth();
  const [accounts, setAccounts] = useState<CustomerAccountOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const userAccountId = userAccount?.id;

    if (!userAccountId) {
      setAccounts([]);
      setLoading(false);
      setError(null);
      return () => {
        mounted = false;
      };
    }

    setAccounts([]);
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const managers = await getManagerListByUserAccountId(userAccountId);
        const resolvedAccounts = await Promise.all(
          managers
            .filter((manager) => Boolean(manager.customerId))
            .map(async (manager) => {
              const customer = await getCustomer(manager.customerId);
              return customer ? toAccountOption(manager, customer) : null;
            }),
        );

        if (!mounted) return;
        const uniqueAccounts = new Map<string, CustomerAccountOption>();
        for (const account of resolvedAccounts) {
          if (account) uniqueAccounts.set(account.customerId, account);
        }
        setAccounts(Array.from(uniqueAccounts.values()));
      } catch (loadError) {
        if (!mounted) return;
        setAccounts([]);
        setError(errorMessage(loadError));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [userAccount?.id]);

  const currentCustomerId = userAccount?.currentCustomerId ?? null;
  const currentCustomerName = useMemo(
    () => accounts.find((account) => account.customerId === currentCustomerId)?.customerName ?? '',
    [accounts, currentCustomerId],
  );

  const switchCustomer = useCallback(async (account: CustomerAccountOption) => {
    if (!userAccount) return;

    try {
      // Match aligned's switchManager guard: do not point the account at a
      // customer that disappeared between loading the menu and selecting it.
      const customer = await getCustomer(account.customerId);
      if (!customer) {
        setError('That customer account is no longer available.');
        return;
      }
      await updateUserAccountFields(userAccount.id, { currentCustomerId: customer.id });
    } catch (switchError) {
      setError(errorMessage(switchError));
    }
  }, [userAccount]);

  return { accounts, currentCustomerId, currentCustomerName, loading, error, switchCustomer };
}
